import { useMemo } from "react";
import { useSelector } from "react-redux";
import { HotelsShopSelectors } from "@b2bportal/core-hotels";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { HotelShopPriceProps } from "@tenant/hotels";

export const useHotelShopPriceSection = (props: HotelShopPriceProps) => {
  const { t, formatFiatCurrency } = useI18nContext();

  const { data: roomProduct, isLoading } = useSelector(
    HotelsShopSelectors.getCheapestRoomProduct
  );

  const totalNights = useSelector(HotelsShopSelectors.getTotalNights);
  const roomCount = useSelector(HotelsShopSelectors.getRoomsCount);
  const perNightPrice = formatFiatCurrency(roomProduct?.perNightTotal.fiat);
  const totalPrice = formatFiatCurrency(roomProduct?.tripTotal.fiat);

  const { isLoading: isRoomsLoading } = useSelector(
    HotelsShopSelectors.getRooms
  );

  const isAvailable = useMemo(() => {
    return !isLoading && roomProduct != null;
  }, [isLoading, roomProduct]);

  return useMemo(() => {
    if (isLoading) {
      return {
        isLoading,
      };
    }
    return {
      isLoading: isLoading,
      isAvailable: isAvailable,
      unavailable: t("core-hotels.shop.price.unavailable"),
      originalPrice: perNightPrice,
      startingAt: t("core-hotels.shop.price.startingAt"),
      price: perNightPrice,
      perNightText: t("core-hotels.shop.price.perNight"),
      pricePerNight: t("core-hotels.shop.price.pricePerNight", {
        price: perNightPrice,
      }),
      taxesAndFeesIncludedDisclaimer: t(
        "core-hotels.roomSelection.taxesAndFeesIncluded"
      ),
      totalPrice:
        (roomCount ?? 0) > 1
          ? t("core-hotels.shop.price.priceForNightsAndRoomsWithTaxesAndFees", {
              price: totalPrice,
              count: totalNights,
              roomCount: roomCount,
            })
          : t("core-hotels.shop.price.priceForNightsWithTaxesAndFees", {
              price: totalPrice,
              count: totalNights,
            }),
      roomCTA: isRoomsLoading
        ? t("loading")
        : t("core-hotels.shop.price.roomCTA"),
    };
  }, [
    isLoading,
    isAvailable,
    t,
    perNightPrice,
    totalPrice,
    totalNights,
    roomCount,
    isRoomsLoading,
  ]);
};
